// Pure per-symbol book mutation helpers extracted from the former bookStore
// singleton. Every algorithm here is the EXACT logic from the old
// bookStore.ts:88-283, refactored to operate on a per-symbol SymbolBookState +
// SymbolBookRuntime instead of reading Zustand get() / module-level globals.
// Re-scoping per symbol is the ONLY change — the TWAF grace window, 5s snapshot
// cadence, bucket aggregation, and sort-on-ingest are byte-for-byte identical.

import {
  type BookLevel,
  type BookSnapshot,
  type SymbolBookState,
  type SymbolBookRuntime,
  SNAPSHOT_INTERVAL_MS,
  MAX_SNAPSHOTS,
  BUCKET_PCT,
  TWAF_VANISH_GRACE_MS,
} from './bookStoreTypes';

// Re-export the shared types/values so consumers can keep importing them from
// either `bookStore` or `bookStoreInternal` without a second import path.
export {
  type BookLevel,
  type BookSnapshot,
  type SymbolBookState,
  type SymbolBookRuntime,
  type BookStoreSet,
  type BookStoreGet,
  createEmptyBookState,
} from './bookStoreTypes';
export { scheduleRenderVersion, cancelRuntime } from './bookStoreRender';

/**
 * Apply a full snapshot to a symbol's book. Kraken emits book snapshots in
 * insertion order, not sorted by price — the DepthChart and best-bid/ask
 * selectors both assume bids[0] is the highest bid and asks[0] is the lowest
 * ask, so we sort on ingest (desc bids / asc asks). Records a periodic
 * aggregated snapshot and returns a NEW SymbolBookState.
 */
export function applySnapshot(
  prev: SymbolBookState,
  bids: BookLevel[],
  asks: BookLevel[],
  runtime: SymbolBookRuntime,
): SymbolBookState {
  const sortedBids = [...bids].sort((a, b) => b.price - a.price);
  const sortedAsks = [...asks].sort((a, b) => a.price - b.price);

  const now = Date.now();
  const bidAges = updateAges(prev.bidAges, sortedBids, runtime.pendingBid, now);
  const askAges = updateAges(prev.askAges, sortedAsks, runtime.pendingAsk, now);

  const next: SymbolBookState = {
    ...prev,
    bids: sortedBids,
    asks: sortedAsks,
    version: prev.version + 1,
    bidAges,
    askAges,
  };
  return maybeRecordSnapshot(next, sortedBids, sortedAsks, runtime);
}

/**
 * Apply incremental deltas to a symbol's book. qty=0 removes a level; sort
 * order is preserved by mergeDelta. Records a periodic snapshot and returns a
 * NEW SymbolBookState.
 */
export function applyDeltaToState(
  prev: SymbolBookState,
  deltaBids: BookLevel[],
  deltaAsks: BookLevel[],
  runtime: SymbolBookRuntime,
): SymbolBookState {
  const bids = mergeDelta([...prev.bids], deltaBids, 'desc');
  const asks = mergeDelta([...prev.asks], deltaAsks, 'asc');

  const now = Date.now();
  const bidAges = updateAges(prev.bidAges, bids, runtime.pendingBid, now);
  const askAges = updateAges(prev.askAges, asks, runtime.pendingAsk, now);

  const next: SymbolBookState = {
    ...prev,
    bids,
    asks,
    version: prev.version + 1,
    bidAges,
    askAges,
  };
  return maybeRecordSnapshot(next, bids, asks, runtime);
}

/** Prepend historical snapshots (from REST), deduping by timestamp. */
export function mergeHistory(
  prev: SymbolBookState,
  snapshots: BookSnapshot[],
): SymbolBookState {
  const existing = new Set(prev.history.map((s) => s.ts));
  const merged = [...prev.history];
  for (const snap of snapshots) {
    if (!existing.has(snap.ts)) {
      merged.push(snap);
      existing.add(snap.ts);
    }
  }
  merged.sort((a, b) => a.ts - b.ts);
  const trimmed =
    merged.length > MAX_SNAPSHOTS
      ? merged.slice(merged.length - MAX_SNAPSHOTS)
      : merged;
  return {
    ...prev,
    history: trimmed,
    historyVersion: prev.historyVersion + 1,
  };
}

/**
 * Maintain per-price firstSeenTs ages with a 5s grace period for vanished
 * levels. New price → stamp now. Returning price (within grace) → keep
 * original stamp. Vanished beyond grace → drop entry. Returns a NEW Map so
 * selectors detect changes via reference.
 */
export function updateAges(
  prev: Map<number, number>,
  current: BookLevel[],
  pendingRemoval: Map<number, number>,
  now: number,
): Map<number, number> {
  const next = new Map(prev);
  const currentPrices = new Set<number>();

  for (const lvl of current) {
    currentPrices.add(lvl.price);
    if (!next.has(lvl.price)) {
      next.set(lvl.price, now);
    }
    // Level is back — clear any pending removal entry.
    pendingRemoval.delete(lvl.price);
  }

  // Track newly vanished levels and prune ones past the grace window.
  for (const price of next.keys()) {
    if (currentPrices.has(price)) continue;
    const vanishedAt = pendingRemoval.get(price) ?? now;
    if (!pendingRemoval.has(price)) {
      pendingRemoval.set(price, now);
    }
    if (now - vanishedAt > TWAF_VANISH_GRACE_MS) {
      next.delete(price);
      pendingRemoval.delete(price);
    }
  }

  return next;
}

/**
 * Record a snapshot every SNAPSHOT_INTERVAL_MS. Aggregates raw price levels
 * into fixed-size buckets for efficient storage. Mutates the runtime's
 * lastSnapshotTs (same as the old module global) and returns either the input
 * state or a NEW state with the appended history.
 */
export function maybeRecordSnapshot(
  state: SymbolBookState,
  bids: BookLevel[],
  asks: BookLevel[],
  runtime: SymbolBookRuntime,
): SymbolBookState {
  const now = Date.now();
  if (now - runtime.lastSnapshotTs < SNAPSHOT_INTERVAL_MS) return state;
  runtime.lastSnapshotTs = now;

  if (bids.length === 0 && asks.length === 0) return state;

  const bestBid = bids[0]?.price ?? 0;
  const bestAsk = asks[0]?.price ?? 0;
  const mid =
    bestBid > 0 && bestAsk > 0 ? (bestBid + bestAsk) / 2 : bestBid || bestAsk;
  if (mid === 0) return state;

  const bucketSize = mid * BUCKET_PCT;

  const bidBuckets = new Map<number, number>();
  for (const l of bids) {
    const bucket = Math.floor(l.price / bucketSize) * bucketSize;
    bidBuckets.set(bucket, (bidBuckets.get(bucket) ?? 0) + l.qty);
  }

  const askBuckets = new Map<number, number>();
  for (const l of asks) {
    const bucket = Math.floor(l.price / bucketSize) * bucketSize;
    askBuckets.set(bucket, (askBuckets.get(bucket) ?? 0) + l.qty);
  }

  const history =
    state.history.length >= MAX_SNAPSHOTS
      ? [...state.history.slice(1), { ts: now, bidBuckets, askBuckets }]
      : [...state.history, { ts: now, bidBuckets, askBuckets }];

  return { ...state, history, historyVersion: state.historyVersion + 1 };
}

/** Apply delta levels to a sorted price array. qty=0 means remove. */
export function mergeDelta(
  levels: BookLevel[],
  deltas: BookLevel[],
  order: 'asc' | 'desc',
): BookLevel[] {
  for (const d of deltas) {
    const idx = levels.findIndex((l) => l.price === d.price);
    if (d.qty === 0) {
      if (idx >= 0) levels.splice(idx, 1);
    } else if (idx >= 0) {
      levels[idx] = d;
    } else {
      const insertIdx = levels.findIndex((l) =>
        order === 'desc' ? l.price < d.price : l.price > d.price,
      );
      if (insertIdx < 0) {
        levels.push(d);
      } else {
        levels.splice(insertIdx, 0, d);
      }
    }
  }
  return levels;
}
